import { mapClientMoneyToServerMoney, parseFloatValue } from '@razz-team/utils';
import {
  ApiAccount,
  ApiCreateStandingOrderRequest,
  ApiStandingOrder,
  ApiStandingOrderDestination,
} from '../../../openapi';
import Decimal from 'decimal.js';

export type StandingOrderOriginFormValues = {
  currency: string | null;
  networkId: string | null;
  accountNumber: string;
  minAmount: string;
  maxAmount: string;
};

export function createStandingOrderOriginFormValues({
  order,
}: {
  order: ApiStandingOrder | undefined;
}): Partial<StandingOrderOriginFormValues> {
  if (!order) {
    return { currency: null, networkId: null };
  }

  let minAmount = '';
  let maxAmount = '';
  switch (order.predicate.amount?.type) {
    case 'Both':
      minAmount = order.predicate.amount.min.amount.toString();
      maxAmount = order.predicate.amount.max.amount.toString();
      break;
    case 'Max':
      maxAmount = order.predicate.amount.amount.amount.toString();
      break;
    case 'Min':
      minAmount = order.predicate.amount.amount.amount.toString();
      break;
  }

  return {
    currency: detectStandingOrderCurrency({ order }),
    networkId: order.predicate.origin.networkId,
    accountNumber: order.predicate.origin.accountNumber,
    minAmount,
    maxAmount,
  };
}

export function createStandingOrderAmount({
  minAmount,
  maxAmount,
  currency,
}: {
  minAmount: string;
  maxAmount: string;
  currency: string;
}): ApiCreateStandingOrderRequest['predicate']['amount'] | undefined {
  if (!currency) return undefined;
  const min = minAmount
    ? mapClientMoneyToServerMoney({
        currency,
        amount: parseFloatValue(minAmount),
      })
    : undefined;
  const max = maxAmount
    ? mapClientMoneyToServerMoney({
        currency,
        amount: parseFloatValue(maxAmount),
      })
    : undefined;
  if (min?.amount && max?.amount) {
    return { type: 'Both', min, max };
  }
  if (min?.amount) {
    return { type: 'Min', amount: min };
  }
  if (max?.amount) {
    return { type: 'Max', amount: max };
  }
  return {
    type: 'Min',
    amount: {
      currency,
      amount: 0,
    },
  };
}

export function detectStandingOrderCurrency({ order }: { order: ApiStandingOrder }) {
  const { amount } = order.predicate;
  switch (amount?.type) {
    case 'Both':
      return amount.min.currency;
    case 'Max':
      return amount.amount.currency;
    case 'Min':
      return amount.amount.currency;
    default:
      return null;
  }
}

export function findStandingOrderDefaultAccount({
  order,
  accounts,
}: {
  order: ApiStandingOrder;
  accounts: ApiAccount[];
}) {
  const currency = detectStandingOrderCurrency({ order });
  if (currency) {
    const account = accounts.find((it) => it.balance.currency === currency);
    if (account) {
      return account;
    }
  }

  return accounts.find((it) => it.purpose === 'BASE');
}

export function calculateMaxDestinationRatio({
  destinations,
}: {
  destinations: ApiStandingOrderDestination[];
}) {
  const ratioLeft = destinations.reduce((acc, it) => acc.minus(it.ratio), new Decimal(1));
  return ratioLeft.gt(0) ? ratioLeft.toNumber() : 0;
}
